import { Button } from '@/components/ui/button';
import { CountrySelect } from '@/components/ui/country-select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { WEBPAGE_TYPE_LABELS } from '@/pages/RegulatoryWatch/labels';
import { ADMIN_PATH, adminPaths, adminRegulatoryWatchPaths } from '@/paths';
import {
  useCancelSourceScrapingService,
  useConfirmSourceScrapingService,
  useSourceScrapingStatusService,
  useStartSourceScrapingService,
} from '@/services/backoffice';
import type {
  CompletedStep,
  DiscardedFeedItem,
  DraftReference,
  DraftSource,
  WebpageType,
} from '@ditto/shared';
import { SCRAPING_STEPS } from '@ditto/shared';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// ── Source type helpers ──

const REFERENCE_TYPE_OPTIONS = [
  { value: 'guidance', label: 'Guidance' },
  { value: 'regulation', label: 'Regulation' },
] as const;

// ── Webpage type descriptions ──

const WEBPAGE_TYPE_DESCRIPTIONS: Record<WebpageType, string> = {
  feed: 'RSS/Atom feed or news page with mixed content (news + regulatory).',
  listing:
    'A page listing regulatory documents (e.g., agency guidance portal). Creates up to one source with multiple references and updates.',
  'single-document':
    'A direct link to a single regulatory document or PDF. Creates up to one source with a unique reference and updates.',
};

// ── Progress steps ──

const ProgressSteps: FC<{
  webpageType: WebpageType;
  currentStep: string;
  completedSteps: CompletedStep[];
}> = ({ webpageType, currentStep, completedSteps }) => {
  const steps = SCRAPING_STEPS[webpageType];
  const completedMap = useMemo(
    () => new Map(completedSteps.map(s => [s.step, s])),
    [completedSteps]
  );

  return (
    <div className="space-y-4">
      {steps.map(({ step, label }) => {
        const completed = completedMap.get(step);
        const isCurrent = step === currentStep && !completed;
        const isDone = !!completed || currentStep === 'done';

        return (
          <div key={step} className="flex items-start gap-3">
            <div className="mt-0.5">
              {isDone ? (
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              ) : isCurrent ? (
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-neutral-300" />
              )}
            </div>
            <div className="flex-1">
              <span
                className={
                  isDone
                    ? 'text-sm text-neutral-700'
                    : isCurrent
                      ? 'text-sm font-medium text-neutral-900'
                      : 'text-sm text-neutral-400'
                }
              >
                {label}
              </span>
              {completed?.output && (
                <div className="mt-1.5 text-sm text-neutral-600 whitespace-pre-line">
                  {completed.output}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Editable source header ──

const EditableSourceHeader: FC<{
  source: DraftSource;
  onChange: (updated: DraftSource) => void;
}> = ({ source, onChange }) => {
  const update = (field: keyof DraftSource, value: string | null) =>
    onChange({ ...source, [field]: value });

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="space-y-1">
        <Label className="text-xs">Title</Label>
        <Input
          value={source.title}
          onChange={e => update('title', e.target.value)}
          className="h-8 text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Geography</Label>
          <CountrySelect
            value={source.geography || null}
            onChange={val => update('geography', val ?? '')}
            placeholder="Select"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Webpage type</Label>
          <div className="h-8 flex items-center text-sm text-muted-foreground px-3 border rounded-md bg-muted/50">
            {WEBPAGE_TYPE_LABELS[source.webpageType] ?? source.webpageType}
          </div>
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">URL</Label>
        <Input
          value={source.url}
          onChange={e => update('url', e.target.value)}
          className="h-8 text-sm"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Description</Label>
        <Textarea
          value={source.description ?? ''}
          onChange={e => update('description', e.target.value || null)}
          className="min-h-[60px] text-sm"
        />
      </div>
    </div>
  );
};

// ── Editable reference row (regulatory items with version) ──

const EditableReferenceRow: FC<{
  reference: DraftReference;
  isFeed: boolean;
  showTypeSelect: boolean;
  selected: boolean;
  onToggle: () => void;
  onChange: (updated: DraftReference) => void;
}> = ({ reference, isFeed, showTypeSelect, selected, onToggle, onChange }) => {
  const [expanded, setExpanded] = useState(false);

  const updateRef = (field: keyof DraftReference, value: unknown) =>
    onChange({ ...reference, [field]: value });

  const updateVersion = (field: string, value: unknown) => {
    onChange({
      ...reference,
      version: { ...reference.version, [field]: value },
    });
  };

  const updateUpdate = (field: string, value: unknown) => {
    if (!reference.update) return;
    onChange({
      ...reference,
      update: { ...reference.update, [field]: value },
    });
  };

  return (
    <div
      className={`border rounded-lg ${selected ? '' : 'opacity-50'} ${expanded ? 'p-4' : 'px-4 py-2'}`}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="h-4 w-4 rounded border-neutral-300 mt-1"
        />
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex-1 flex items-start gap-2 text-left min-w-0"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">
                {reference.title}
              </span>
              {reference.version.file && (
                <FileText className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
              )}
            </div>
            {!expanded && (
              <div className="flex items-center gap-2 mt-0.5">
                {reference.update?.publicationDate && (
                  <span className="text-xs text-muted-foreground shrink-0">
                    {reference.update.publicationDate.substring(0, 10)}
                  </span>
                )}
              </div>
            )}
          </div>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0" />
          )}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 space-y-3 pl-7">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Title</Label>
              <Input
                value={reference.title}
                onChange={e => updateRef('title', e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            {isFeed && (
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select
                  value={reference.type ?? 'guidance'}
                  onValueChange={v => updateRef('type', v)}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REFERENCE_TYPE_OPTIONS.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {showTypeSelect && (
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select
                  value={reference.type ?? 'guidance'}
                  onValueChange={v =>
                    updateRef('type', v as 'guidance' | 'regulation')
                  }
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REFERENCE_TYPE_OPTIONS.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">External ID</Label>
              <Input
                value={reference.externalId}
                onChange={e => updateRef('externalId', e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">URL</Label>
              <Input
                value={reference.url ?? ''}
                onChange={e => updateRef('url', e.target.value || null)}
                className="h-8 text-sm"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Textarea
              value={reference.description ?? ''}
              onChange={e => updateRef('description', e.target.value || null)}
              className="min-h-[40px] text-sm"
            />
          </div>

          {/* Version fields */}
          <div className="border-t pt-3">
            <p className="text-xs font-medium text-neutral-500 mb-2">Version</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select
                  value={reference.version.status}
                  onValueChange={v => updateVersion('status', v)}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="final">Final</SelectItem>
                    <SelectItem value="withdrawn">Withdrawn</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Effective date</Label>
                <Input
                  type="date"
                  value={
                    reference.version.effectiveDate?.substring(0, 10) ?? ''
                  }
                  onChange={e =>
                    updateVersion(
                      'effectiveDate',
                      e.target.value ? e.target.value : null
                    )
                  }
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">File</Label>
                <div className="h-8 flex items-center text-xs text-muted-foreground px-3 border rounded-md bg-muted/50">
                  {reference.version.file
                    ? `PDF (${(reference.version.file.size / 1024).toFixed(0)} KB)`
                    : 'No file'}
                </div>
              </div>
            </div>
          </div>

          {/* Update fields */}
          {reference.update ? (
            <div className="border-t pt-3">
              <p className="text-xs font-medium text-neutral-500 mb-2">
                Update
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Kind</Label>
                  <Select
                    value={reference.update.kind}
                    onValueChange={v => updateUpdate('kind', v)}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regulatory_update">
                        Regulatory update
                      </SelectItem>
                      <SelectItem value="news">News</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Update type</Label>
                  <Select
                    value={reference.update.updateType ?? '__none__'}
                    onValueChange={v =>
                      updateUpdate('updateType', v === '__none__' ? null : v)
                    }
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="revision">Revision</SelectItem>
                      <SelectItem value="withdrawal">Withdrawal</SelectItem>
                      <SelectItem value="status_change">
                        Status change
                      </SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Publication date</Label>
                  <Input
                    type="date"
                    value={
                      reference.update.publicationDate?.substring(0, 10) ?? ''
                    }
                    onChange={e =>
                      updateUpdate('publicationDate', e.target.value)
                    }
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="border-t pt-3">
              <p className="text-xs text-muted-foreground italic">
                No update — document date is before the cutoff date.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main page ──

type PageStep = 'input' | 'discovery' | 'preview' | 'done';

const defaultCutoffDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split('T')[0]!;
};

export const SourceScrapingPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sourcesPath = `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.sources}`;

  // Restore from URL params on mount (survives sleep/wake remounts)
  const paramWorkflowId = searchParams.get('workflowId');
  const paramWebpageType = searchParams.get(
    'webpageType'
  ) as WebpageType | null;

  const [step, setStep] = useState<PageStep>(
    paramWorkflowId ? 'discovery' : 'input' // refined in useEffect once status loads
  );
  const [url, setUrl] = useState('');
  const [webpageType, setWebpageType] = useState<WebpageType | ''>(
    paramWebpageType ?? ''
  );
  const [cutoffDate, setCutoffDate] = useState(defaultCutoffDate);
  const [workflowId, setWorkflowId] = useState<string | null>(paramWorkflowId);
  const [error, setError] = useState<string | null>(null);

  // Preview state
  const [sourceTemplate, setSourceTemplate] = useState<DraftSource | null>(
    null
  );
  const [editedReferences, setEditedReferences] = useState<DraftReference[]>(
    []
  );
  const [selectedRefs, setSelectedRefs] = useState<Set<number>>(new Set());
  const [discardedItems, setDiscardedItems] = useState<DiscardedFeedItem[]>([]);

  // Confirm result
  const [confirmResult, setConfirmResult] = useState<{
    sourceIds: string[];
    referencesCreated: number;
    updatesCreated: number;
  } | null>(null);

  const { startScraping, isPending: isStarting } =
    useStartSourceScrapingService();
  const { status } = useSourceScrapingStatusService(
    workflowId,
    step === 'discovery' || step === 'preview'
  );
  const { confirm, isPending: isConfirming } =
    useConfirmSourceScrapingService();
  const { cancel } = useCancelSourceScrapingService();

  // Auto-transition to preview when workflow completes
  useEffect(() => {
    if (!status) return;
    if (step !== 'discovery' && step !== 'preview') return;

    if (status.workflowStatus === 'COMPLETED' && status.result) {
      if (step !== 'preview') {
        setSourceTemplate(status.result.source);
        const refs = status.result.references;
        const discarded = status.result.discardedItems ?? [];
        setEditedReferences(refs);
        setSelectedRefs(new Set(refs.map((_, i) => i)));
        setDiscardedItems(discarded);

        if (refs.length === 0) {
          setError('Discovery completed but found no references.');
        }
        setStep('preview');
      }
    } else if (status.workflowStatus === 'FAILED') {
      setError(status.error ?? 'Discovery workflow failed');
    } else if (status.workflowStatus === 'CANCELLED') {
      setError('Discovery was cancelled');
    }
  }, [step, status]);

  const handleStart = useCallback(async () => {
    if (!url || !webpageType) return;
    setError(null);
    try {
      const result = await startScraping({ url, webpageType, cutoffDate });
      setWorkflowId(result.workflowId);
      setStep('discovery');
      setSearchParams(
        { workflowId: result.workflowId, webpageType },
        { replace: true }
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start scraping');
    }
  }, [url, webpageType, cutoffDate, startScraping, setSearchParams]);

  const handleCancel = useCallback(() => {
    if (workflowId) cancel(workflowId);
    navigate(sourcesPath);
  }, [workflowId, cancel, navigate, sourcesPath]);

  const totalSelected = selectedRefs.size;

  const handleConfirm = useCallback(async () => {
    if (!sourceTemplate) return;
    const selRefs = editedReferences.filter((_, i) => selectedRefs.has(i));
    try {
      const result = await confirm({
        source: sourceTemplate,
        references: selRefs,
        discardedItems: discardedItems.length > 0 ? discardedItems : undefined,
      });
      setConfirmResult(result);
      setStep('done');
      setSearchParams({}, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to confirm');
    }
  }, [
    sourceTemplate,
    editedReferences,
    selectedRefs,
    discardedItems,
    confirm,
    setSearchParams,
  ]);

  const updateReference = (index: number, updated: DraftReference) => {
    setEditedReferences(prev =>
      prev.map((r, i) => (i === index ? updated : r))
    );
  };

  const toggleRef = (index: number) => {
    setSelectedRefs(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(sourcesPath)}
          className="h-8 w-8"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold">
          {step === 'input' && 'Scrape New Source'}
          {step === 'discovery' && 'Discovery in Progress'}
          {step === 'preview' && 'Review & Edit'}
          {step === 'done' && 'Done'}
        </h1>
      </div>

      {/* Step 1: Input */}
      {step === 'input' && (
        <div className="max-w-2xl space-y-6">
          <div className="space-y-2">
            <Label>URL</Label>
            <Input
              placeholder="https://example.com/regulatory-page"
              value={url}
              onChange={e => setUrl(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Webpage type</Label>
            <div className="space-y-0.5">
              {(['feed', 'listing', 'single-document'] as const).map(wt => (
                <p key={wt} className="text-xs text-muted-foreground">
                  <span className="font-medium">
                    {wt === 'feed'
                      ? 'Feed'
                      : wt === 'listing'
                        ? 'Listing'
                        : 'Single document'}
                  </span>
                  {' — '}
                  {WEBPAGE_TYPE_DESCRIPTIONS[wt]}
                </p>
              ))}
            </div>
            <Select
              value={webpageType}
              onValueChange={v => setWebpageType(v as WebpageType)}
            >
              <SelectTrigger className="w-56">
                <SelectValue placeholder="Select webpage type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="feed">Feed</SelectItem>
                <SelectItem value="listing">Listing</SelectItem>
                <SelectItem value="single-document">Single document</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Cutoff date</Label>
            <Input
              type="date"
              value={cutoffDate}
              onChange={e => setCutoffDate(e.target.value)}
              className="w-56"
            />
            <p className="text-xs text-muted-foreground">
              Only articles/documents published after this date will be
              included.
            </p>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate(sourcesPath)}>
              Cancel
            </Button>
            <Button
              onClick={handleStart}
              disabled={!url || !webpageType || !cutoffDate || isStarting}
            >
              {isStarting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Starting...
                </>
              ) : (
                'Start Discovery'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Discovery progress */}
      {step === 'discovery' && (
        <div className="max-w-2xl space-y-6">
          {webpageType && (
            <ProgressSteps
              webpageType={webpageType as WebpageType}
              currentStep={status?.progress?.step ?? 'infer_sources'}
              completedSteps={status?.progress?.completedSteps ?? []}
            />
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button variant="outline" onClick={handleCancel}>
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
        </div>
      )}

      {/* Step 3: Preview + Edit */}
      {step === 'preview' && (
        <div className="max-w-3xl space-y-6">
          {sourceTemplate && (
            <div>
              <h3 className="text-sm font-semibold mb-3">Source</h3>
              <EditableSourceHeader
                source={sourceTemplate}
                onChange={setSourceTemplate}
              />
            </div>
          )}

          {/* References section */}
          {editedReferences.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">
                  References ({selectedRefs.size} of {editedReferences.length}{' '}
                  selected)
                </h3>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setSelectedRefs(
                        new Set(editedReferences.map((_, i) => i))
                      )
                    }
                  >
                    Select all
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedRefs(new Set())}
                  >
                    Deselect all
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {editedReferences.map((ref, i) => (
                  <EditableReferenceRow
                    key={i}
                    reference={ref}
                    isFeed={sourceTemplate?.webpageType === 'feed'}
                    showTypeSelect={sourceTemplate?.webpageType !== 'feed'}
                    selected={selectedRefs.has(i)}
                    onToggle={() => toggleRef(i)}
                    onChange={updated => updateReference(i, updated)}
                  />
                ))}
              </div>
            </div>
          )}

          {discardedItems.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {discardedItems.length} item
              {discardedItems.length !== 1 ? 's' : ''} discarded
            </p>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate(sourcesPath)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={totalSelected === 0 || isConfirming}
            >
              {isConfirming ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Creating...
                </>
              ) : (
                `Confirm & Create (${totalSelected})`
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Done */}
      {step === 'done' && confirmResult && (
        <div className="max-w-lg space-y-6">
          <div className="border rounded-lg p-8 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-lg font-medium">Sources created</p>
            <p className="text-sm text-muted-foreground">
              {confirmResult.sourceIds.length} source(s),{' '}
              {confirmResult.referencesCreated} reference(s),{' '}
              {confirmResult.updatesCreated} update(s)
            </p>
          </div>
          <Button onClick={() => navigate(sourcesPath)}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Sources
          </Button>
        </div>
      )}
    </div>
  );
};
